import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ChevronRight,
  ChevronLeft,
  X,
  User,
  Camera
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AvatarUpload } from "./AvatarUpload";

interface TourStep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  icon_name: string;
  action_label: string | null;
  action_route: string | null;
  is_active: boolean;
}

interface TourStepDisplay {
  title: string;
  description: string;
  icon: React.ReactNode;
  action?: {
    label: string;
    handler: () => void;
  };
  isProfileStep?: boolean;
  isAvatarStep?: boolean;
}

const ONBOARDING_STORAGE_KEY = "scope-manager-onboarding-completed";

export const OnboardingTour: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: dbSteps, isLoading } = useQuery({
    queryKey: ["onboarding-tour-steps"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("onboarding_tour_steps")
        .select("*")
        .eq("is_active", true)
        .order("step_number");
      
      if (error) throw error;
      return data as TourStep[];
    },
  });

  // Check onboarding completion status and load profile data
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["profile-onboarding-status"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      setUserId(user.id);
      
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarding_completed, display_name, avatar_url")
        .eq("id", user.id)
        .single();
      
      if (error) throw error;
      
      // Pre-populate form if profile exists
      if (data) {
        setDisplayName(data.display_name || "");
        setAvatarUrl(data.avatar_url || null);
      }
      
      return data;
    },
  });

  // Update onboarding completion in database
  const updateOnboardingMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("User ID not found");
      
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Also store in localStorage as backup
      localStorage.setItem(ONBOARDING_STORAGE_KEY, "true");
      queryClient.invalidateQueries({ queryKey: ["profile-onboarding-status"] });
    },
    onError: (error) => {
      console.error("Error updating onboarding status:", error);
      toast({
        title: "Error",
        description: "Failed to save onboarding progress",
        variant: "destructive",
      });
      // Fallback to localStorage only
      localStorage.setItem(ONBOARDING_STORAGE_KEY, "true");
    },
  });

  useEffect(() => {
    // Check both database and localStorage (for offline support)
    const hasCompletedOnboarding = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    
    if (!hasCompletedOnboarding && 
        !isLoading && 
        !profileLoading && 
        profile && 
        !profile.onboarding_completed && 
        dbSteps && 
        dbSteps.length > 0) {
      // Show unified onboarding after a short delay
      setTimeout(() => setOpen(true), 1500);
    }
  }, [isLoading, profileLoading, profile, dbSteps]);

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      toast({
        title: "Display name required",
        description: "Please enter your display name to continue",
        variant: "destructive",
      });
      return;
    }

    if (!userId) return;

    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ display_name: displayName.trim() })
        .eq("id", userId);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been set up successfully!",
      });

      // Move to next step
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  // Convert database steps to display format with profile setup steps first
  const steps: TourStepDisplay[] = React.useMemo(() => {
    const profileSetupSteps: TourStepDisplay[] = [
      {
        title: "Complete Your Profile",
        description: "Add your name to personalize your experience",
        icon: <User className="h-12 w-12 text-primary" />,
        isProfileStep: true,
      },
      {
        title: "Add Profile Picture",
        description: "Upload your profile picture (optional)",
        icon: <Camera className="h-12 w-12 text-primary" />,
        isAvatarStep: true,
      },
    ];

    if (!dbSteps) return profileSetupSteps;
    
    const featureTourSteps = dbSteps.map(step => {
      const Icon = (LucideIcons as any)[step.icon_name] || LucideIcons.Sparkles;
      
      return {
        title: step.title,
        description: step.description,
        icon: <Icon className="h-12 w-12 text-primary" />,
        action: step.action_label && step.action_route ? {
          label: step.action_label,
          handler: () => navigate(step.action_route!),
        } : undefined,
      };
    });

    return [...profileSetupSteps, ...featureTourSteps];
  }, [dbSteps, navigate]);

  const handleNext = () => {
    const step = steps[currentStep];
    
    // If on profile step, validate and save before proceeding
    if (step?.isProfileStep) {
      handleSaveProfile();
      return;
    }
    
    // Normal progression for other steps
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    updateOnboardingMutation.mutate();
    setOpen(false);
  };

  const handleSkip = () => {
    updateOnboardingMutation.mutate();
    setOpen(false);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];

  if (isLoading || profileLoading || !steps || steps.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleSkip();
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">
              {currentStep === 0 ? "Welcome to Scope Manager!" : step.title}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkip}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {currentStep === 0 && (
            <DialogDescription>
              Let's get you set up in just a few steps
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="py-6 space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            {step.icon}
          </div>

          {/* Description */}
          <div className="text-center space-y-2">
            {currentStep > 0 && (
              <h3 className="text-lg font-semibold">{step.title}</h3>
            )}
            <p className="text-sm text-muted-foreground">
              {step.description}
            </p>
          </div>

          {/* Profile Setup Form */}
          {step.isProfileStep && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  placeholder="Enter your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSaveProfile();
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Avatar Upload Step */}
          {step.isAvatarStep && userId && (
            <div className="flex justify-center">
              <AvatarUpload
                currentAvatarUrl={avatarUrl}
                userDisplayName={displayName || "User"}
                userId={userId}
                onAvatarUpdate={(url) => setAvatarUrl(url)}
              />
            </div>
          )}

          {/* Action Button for database-driven steps */}
          {step.action && !step.isProfileStep && !step.isAvatarStep && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  step.action?.handler();
                  handleComplete();
                }}
              >
                {step.action.label}
              </Button>
            </div>
          )}

          {/* Progress */}
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-center text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </p>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSkip}>
              {currentStep < 2 ? "Skip setup" : "Skip Tour"}
            </Button>
            <Button 
              onClick={handleNext} 
              className="gap-2"
              disabled={step.isProfileStep && savingProfile}
            >
              {savingProfile ? "Saving..." : currentStep === steps.length - 1 ? "Get Started" : step.isProfileStep ? "Continue" : step.isAvatarStep ? "Continue" : "Next"}
              {currentStep !== steps.length - 1 && !step.isProfileStep && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper to reset onboarding (for testing)
export const resetOnboarding = async (userId: string) => {
  // Clear localStorage
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  
  // Reset database field
  await supabase
    .from("profiles")
    .update({ onboarding_completed: false })
    .eq("id", userId);
};
